import { useState, useEffect, useCallback, type KeyboardEvent } from 'react';
import { Search, X, AlertCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { DataTableSearchProps } from './types';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DEFAULT_PLACEHOLDER = 'Search...';
const MIN_SEARCH_LENGTH = 2;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DataTableSearch({
  value,
  onChange,
  placeholder = DEFAULT_PLACEHOLDER,
}: DataTableSearchProps) {
  const [localValue, setLocalValue] = useState(value);
  const [showErrorModal, setShowErrorModal] = useState(false);

  // Sync local value when prop changes externally
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(e.target.value);
  }, []);

  // Check if search term meets minimum length requirement (empty string is allowed to clear)
  const canSubmit = localValue === '' || localValue.length >= MIN_SEARCH_LENGTH;

  const handleSubmit = useCallback(() => {
    if (canSubmit) {
      onChange(localValue);
    } else {
      setShowErrorModal(true);
    }
  }, [localValue, onChange, canSubmit]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  const handleClear = useCallback(() => {
    setLocalValue('');
    onChange('');
  }, [onChange]);

  // Determine if submit button should be disabled (no changes to submit)
  const isSubmitDisabled = localValue === value;

  return (
    <>
      <div className="relative flex items-center gap-2" data-testid="data-table-search">
        <div className="relative flex-1">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-v11-text-muted"
            data-testid="search-icon"
          />
          <Input
            type="text"
            value={localValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="pl-9 pr-9 bg-v11-surface border-v11-border text-v11-text-primary placeholder:text-v11-text-muted"
          />
          {localValue.length > 0 && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute right-3 top-1/2 -translate-y-1/2 flex h-4 w-4 items-center justify-center text-v11-text-muted hover:text-v11-text"
              data-testid="clear-search"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
          size="sm"
          variant="default"
          data-testid="search-submit"
          className="px-2.5"
          aria-label="Submit search"
        >
          <Search className="h-4 w-4" />
        </Button>
      </div>

      <AlertDialog open={showErrorModal} onOpenChange={setShowErrorModal}>
        <AlertDialogContent data-testid="search-error-modal">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-500" />
              Search too short
            </AlertDialogTitle>
            <AlertDialogDescription>
              Please enter at least {MIN_SEARCH_LENGTH} characters to search.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction data-testid="search-error-modal-ok">OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
